import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { publicApi } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Smartphone, ShieldCheck } from "lucide-react";

const inputCls = "w-full bg-[#0A0A0A] border border-white/15 rounded px-4 py-3.5 text-base text-[#F5F2EB] placeholder-[#52525B] focus:outline-none focus:border-[#D4AF37]";

const AUDIENCES = {
  driver: {
    endpoint: "/auth/driver",
    overline: "Driver App",
    title: "Chauffeur sign in",
    dest: "/driver",
    helper: "We'll text a 6-digit code to the phone number dispatch has on file for you.",
    footer: [
      { text: "Dispatcher or password user?", label: "Classic sign in", to: "/login", testid: "driver-login-classic-link" },
      { text: "Want to drive with us?", label: "Apply here", to: "/drive-with-us" },
    ],
  },
  customer: {
    endpoint: "/auth/customer",
    overline: "Passenger App",
    title: "Passenger sign in",
    dest: "/customer",
    helper: "We'll text a 6-digit code to the phone number from your reservations — no password needed.",
    footer: [
      { text: "Prefer email & password?", label: "Classic sign in", to: "/login", testid: "customer-login-classic-link" },
      { text: "New here?", label: "Book your first ride", to: "/book" },
    ],
  },
};

/** Passwordless sign-in: phone or email → 6-digit SMS code.
 *  Mobile-first — the front door of the installable PWA for both audiences. */
export default function DriverLogin({ audience = "driver" }) {
  const cfg = AUDIENCES[audience] || AUDIENCES.driver;
  const { refresh } = useAuth();
  const [step, setStep] = useState(1);
  const [identifier, setIdentifier] = useState("");
  const [code, setCode] = useState("");
  const [pending, setPending] = useState(null); // {pending_token, phone_hint}
  const [busy, setBusy] = useState(false);
  const codeRef = useRef(null);

  const requestCode = async (e) => {
    e?.preventDefault();
    if (!identifier.trim()) { toast.error("Enter your phone number or email"); return; }
    setBusy(true);
    try {
      const r = await publicApi.post(`${cfg.endpoint}/request-otp`, { identifier });
      setPending(r.data);
      setStep(2);
      toast.success(r.data.message);
      setTimeout(() => codeRef.current?.focus(), 150);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send the code — try again");
    } finally { setBusy(false); }
  };

  const verify = async (e) => {
    e?.preventDefault();
    if (code.trim().length !== 6) { toast.error("Enter the 6-digit code"); return; }
    setBusy(true);
    try {
      await publicApi.post(`${cfg.endpoint}/verify-otp`,
        { pending_token: pending.pending_token, code: code.trim() },
        { withCredentials: true });
      await refresh?.();
      toast.success("Welcome back!");
      window.location.href = cfg.dest; // full reload so auth context picks up the session cookie
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not verify the code");
      setBusy(false);
    }
  };

  const resend = async () => {
    try {
      await publicApi.post("/auth/resend-otp", { pending_token: pending.pending_token });
      toast.success("A new code was texted");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not resend");
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-[#F5F2EB] flex flex-col">
      <div className="flex-1 flex items-center justify-center px-6">
        <div className="w-full max-w-sm" data-testid="driver-login-page">
          <div className="text-center mb-10">
            <img src="/icons/icon-192.png" alt="Delux Limo" className="w-16 h-16 mx-auto mb-4 rounded-2xl border border-white/10"/>
            <div className="overline text-[#D4AF37] mb-2">{cfg.overline}</div>
            <h1 className="font-serif text-3xl">{cfg.title}</h1>
          </div>

          {step === 1 && (
            <form onSubmit={requestCode} className="space-y-4">
              <div>
                <label className="block text-[10px] uppercase tracking-[0.22em] text-[#A1A1AA] mb-2">Phone number or email</label>
                <input
                  data-testid="driver-login-identifier"
                  className={inputCls}
                  value={identifier}
                  onChange={(e) => setIdentifier(e.target.value)}
                  placeholder="(414) 555-0123"
                  autoComplete="tel"
                  inputMode="tel"
                />
              </div>
              <button data-testid="driver-login-send-code" disabled={busy} className="btn-gold w-full !py-4 text-sm flex items-center justify-center gap-2">
                <Smartphone size={16}/> {busy ? "Sending…" : "Text me a sign-in code"}
              </button>
              <p className="text-[11px] text-[#71717a] text-center leading-relaxed pt-2">
                {cfg.helper}
              </p>
            </form>
          )}

          {step === 2 && (
            <form onSubmit={verify} className="space-y-4">
              <div className="text-center text-sm text-[#A1A1AA] mb-2">
                Code sent to <span className="text-[#F5F2EB]">{pending?.phone_hint}</span>
              </div>
              <input
                ref={codeRef}
                data-testid="driver-login-code"
                className={`${inputCls} text-center tracking-[0.5em] text-2xl font-semibold`}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="••••••"
                inputMode="numeric"
                autoComplete="one-time-code"
              />
              <button data-testid="driver-login-verify" disabled={busy} className="btn-gold w-full !py-4 text-sm flex items-center justify-center gap-2">
                <ShieldCheck size={16}/> {busy ? "Verifying…" : "Sign in"}
              </button>
              <div className="flex justify-between text-[11px] text-[#71717a] pt-1">
                <button type="button" data-testid="driver-login-back" onClick={() => { setStep(1); setCode(""); }} className="hover:text-[#D4AF37]">← Different number</button>
                <button type="button" data-testid="driver-login-resend" onClick={resend} className="hover:text-[#D4AF37]">Resend code</button>
              </div>
            </form>
          )}

          <div className="text-center text-[11px] text-[#71717a] mt-10 space-y-2">
            {cfg.footer.map((f) => (
              <p key={f.to}>{f.text} <Link to={f.to} className="text-[#D4AF37] hover:underline" data-testid={f.testid}>{f.label}</Link></p>
            ))}
          </div>
        </div>
      </div>
      <div className="py-5 text-center text-[10px] text-[#52525B] tracking-[0.18em] uppercase">
        Dispatch · (414) 333-6275
      </div>
    </div>
  );
}
